import queryString from 'query-string';
import { format } from 'date-fns/format';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NavMenu,
  NavMenuButton,
  NavMenuLink,
  PopupContext,
  useModal
} from 'cat-ecommerce-alloy';
import { useSelector } from 'react-redux';
import endpoints from '@app/constants/endpoints';
import { FILE_FORMATS, USER_TYPE_GUEST } from '@app/constants/commonConstants';
import { replaceTokensInUrl } from '@app/utils';
import { SAVE_TYPE } from '../../constants';
import AddSavedListModal from '@app/components/saved-lists/AddSavedListModal';
import CreateSavedListModal from '@app/components/saved-lists/CreateSavedListModal';
import useSavedLists from '@app/components/saved-lists/useSavedLists';

const OrderItemsHeaderActionsItems = () => {
  const [t] = useTranslation();
  const langId = useSelector(s => s.common.langId);
  const storeId = useSelector(s => s.common.storeId);
  const menuCtx = useContext(PopupContext);
  const orderId = useSelector(s => s.orders.current);
  const savedLists = useSelector(s => s.account?.savedLists);
  const current = useSelector(s => s.orders.current);
  const ascendingOrderItems = useSelector(
    s => s.orders.byId?.[current]?.ascendingOrderItems
  );
  const userType = useSelector(s => s.common?.userType);
  const userLogonId = useSelector(s => s.common?.logonId);
  const isGuest = userType === USER_TYPE_GUEST;
  const { saveItemsToList } = useSavedLists();

  const orderItems = ascendingOrderItems?.map(
    ({ partNumber, quantity, name, unformattedUnitPrice }) => ({
      partNumber,
      quantity,
      name,
      price: unformattedUnitPrice
    })
  );
  const hasSavedLists = savedLists?.length > 0;
  const close = () => {
    menuCtx.closePopup();
  };

  // Save timeStamp with memo to avoid changing each second
  const exportTimestamp = useMemo(
    () => format(new Date(), 'MMddyy-HHmmss'),
    []
  );
  const getFilename = detailed => {
    // Updating filename with Username-Filename-Filedate-Timestamp - AzDO #469476
    const exportTypeName = detailed ? 'Detailed Export' : 'Cart Export';
    return `${userLogonId}-${exportTypeName}-${exportTimestamp}`;
  };
  const getOrderConfirmationUrl = (fileFormat, detailed = false) => {
    const fromPage = 'ShoppingCart';
    const fileName = getFilename(detailed);
    const baseUrl = replaceTokensInUrl(
      endpoints.ORDER_EXPORT_LINK,
      storeId,
      langId,
      fileName,
      orderId,
      fileFormat,
      fromPage
    );

    return `${baseUrl}&${queryString.stringify({ detailed })}`;
  };

  const AddSavedListModalProps = useModal();
  const createSavedListModalProps = useModal();
  const data = {
    items: orderItems,
    saveType: SAVE_TYPE.SAVE_ALL
  };

  const handleSaveToList = () => {
    if (isGuest) {
      saveItemsToList(data);
    } else {
      if (hasSavedLists) {
        AddSavedListModalProps.open();
      } else {
        createSavedListModalProps.open();
      }
    }
  };
  const downloadUrls = [
    {
      url: getOrderConfirmationUrl(FILE_FORMATS.csv),
      filename: getFilename(),
      displayName: 'SHP_CART_SAVE_CSV'
    },
    {
      url: getOrderConfirmationUrl(FILE_FORMATS.xls),
      filename: getFilename(),
      displayName: 'SHP_CART_SAVE_XLS'
    },
    {
      handlClick: handleSaveToList,
      displayName: 'SAVE_TO_LIST'
    },
    {
      url: getOrderConfirmationUrl(FILE_FORMATS.csv, true),
      filename: getFilename(true),
      displayName: 'SHP_CART_SAVE_DETAILED_CSV'
    },
    {
      url: getOrderConfirmationUrl(FILE_FORMATS.xls, true),
      filename: getFilename(true),
      displayName: 'SHP_CART_SAVE_DETAILED_XLS'
    }
  ];

  const actionClassName = 'pe-5 py-2 text-uppercase line-height-16';
  return (
    <>
      <AddSavedListModal {...AddSavedListModalProps} addSavedListData={data} />
      <CreateSavedListModal
        {...createSavedListModalProps}
        createSavedListData={data}
      />
      <NavMenu>
        {downloadUrls.map(({ handlClick, url, displayName, filename }) =>
          url ? (
            <NavMenuLink
              className="mb-0"
              download={filename}
              key={url}
              href={url}
              linkClassName={actionClassName}
              onClick={close}
              rel="noopener noreferrer"
              target="_blank"
            >
              {t(displayName)}
            </NavMenuLink>
          ) : (
            <NavMenuButton
              className="mb-0"
              key={displayName}
              linkClassName={actionClassName}
              onClick={handlClick}
            >
              {t(displayName)}
            </NavMenuButton>
          )
        )}
      </NavMenu>
    </>
  );
};

export default OrderItemsHeaderActionsItems;
